import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAcl from "../../hooks/useAcl";

export type InfoModalType = "task" | "meeting" | "project";

export interface TaskData {
  id: string;
  name: string;
  status: string;
  assignee: string;
  start: string;
  end?: string;
  description: string;
}

export interface MeetingData {
  id: string;
  name: string;
  status: string;
  assignee: string;
  start: string;
  end?: string;
  description: string;
}

export interface ProjectData {
  id: string;
  name: string;
  status: string;
  start?: string;
  end?: string;
  description?: string;
}

export interface InfoModalProps {
  open: boolean;
  onClose: () => void;
  type: InfoModalType;
  data: any;
}

const InfoModal: React.FC<InfoModalProps> = ({ open, onClose, type, data }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { canRead: canReadProjects } = useAcl("Project");

  const handleDetails = () => {
    if (!data?.id) {
      console.error("ID is undefined in InfoModal data:", data);
      return;
    }
    
    if (type === "project" && canReadProjects === false) {
      console.warn("Нет доступа к проектам");
      onClose();
      return;
    }
    
    onClose();
    const prefix = type === "task" ? "/task" : type === "meeting" ? "/meeting" : "/project";
    navigate(`${prefix}/${data.id}`);
  };

  const normalizedStatus = (data?.status || "")
    .toLowerCase()
    .replace(/\s/g, "_");
  const translatedStatus = type === "project" 
    ? t(`statuses.project.${normalizedStatus}`, data?.status || "")
    : t(`statuses.${type}.${normalizedStatus}`);

  const formatDateTime = (dateStr: string | undefined | null, isProject: boolean = false) => {
    if (!dateStr) return "";
    if (isProject) {
      return dateStr.split(" ")[0];
    }
    return dateStr.replace(" ", "T").slice(0, 16);
  };

  const startDate = data?.start || data?.dateStart || "";
  const endDate = data?.end || data?.dateEnd || "";
  const assignee = type === "project" ? undefined : (data?.assignee || data?.assignedUserName || "");
  const isProject = type === "project";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {type === "task"
          ? t("tasks.detail.detailsTitleTask")
          : type === "meeting"
          ? t("meetingDetail.detailsTitleMeeting")
          : t("projects.detail.detailsTitleProject") || "Информация о проекте"}
      </DialogTitle>
      <DialogContent>
        <TextField
          label={t("tasks.detail.nameLabel")}
          fullWidth
          margin="normal"
          value={data?.name || ""}
          InputProps={{ readOnly: true }}
        />

        <TextField
          label={t("tasks.detail.statusLabel")}
          fullWidth
          margin="normal"
          value={translatedStatus || data?.status || ""}
          InputProps={{ readOnly: true }}
        />

        {type !== "project" && (
          <TextField
            label={t("tasks.detail.assigneeLabel")}
            fullWidth
            margin="normal"
            value={assignee || ""}
            InputProps={{ readOnly: true }}
          />
        )}

        <Box display="flex" flexDirection="column" gap={2} mt={2}>
          <TextField
            label={t("tasks.detail.startDateLabel")}
            type={isProject ? "date" : "datetime-local"}
            fullWidth
            value={formatDateTime(startDate, isProject)}
            InputLabelProps={{ shrink: true }}
            InputProps={{ readOnly: true }}
          />
          <TextField
            label={t("tasks.detail.endDateLabel")}
            type={isProject ? "date" : "datetime-local"}
            fullWidth
            value={formatDateTime(endDate, isProject)}
            InputLabelProps={{ shrink: true }}
            InputProps={{ readOnly: true }}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          {t("tasks.detail.backButton")}
        </Button>
        <Button
          variant="contained"
          onClick={handleDetails}
          sx={{ textTransform: "none" }}
        >
          {t("tasks.detail.detailsButton")}{" "}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InfoModal;
